import {graphqlQuery} from '../utils/graphqlQuery';
import {log} from '../api/logger';

const OPERATION_NAME = 'graphql_get_quote';
const MAX_ATTEMPTS = 5;
const RETRY_DELAY = 1000;

/* istanbul ignore next */
const quoteQuery = `
    query quote($createCheckoutRequest: CreateCheckoutRequest) {
        quote(createCheckoutRequest: $createCheckoutRequest) {
            policies{
                type
                damageLiability {
                    damageLiabilityAmount {
                        localized
                    }
                }
            }
            priceDetails {
                notes {
                    title
                    description
                }
                totalNumeric {
                    totalInCents
                }
                totals {
                    title
                    amount
                    tooltip
                    subItems {
                        title
                    }
                }
                checkoutUrl
                instantBooking
                dueNow {
                    title
                    amount
                    tooltips {
                        title
                        amount
                        tooltip
                    }
                }
                payments {
                    title
                    amount
                    paidText
                    infoText
                    status
                    viewUrl
                }
                averageNightlies {
                    perNightCost {
                        currency
                        amount
                        localized
                    }
                    type
                }
                edapEventJson
                edapQuoteSuccess
                edapPriceQuotePresented
                lineItems {
                    title
                    amount
                    mixedCurrencyDisclaimer
                    type
                    tooltip
                    subItems {
                        title
                        amount
                        type
                        tooltip
                        subItems {
                            title
                            amount
                        }
                    }
                }
            }
        }
    }
`;

/* eslint-disable complexity */
async function quoteGraphql(
    {
        unit,
        listingId,
        pointOfSaleId,
        estimatedCurrency,
        arrivalDate,
        departureDate,
        adults,
        childAges,
        pets = 0
    },
    locale = '',
    store
) {
    const quoteOptions = {
        unit,
        listingId,
        pointOfSaleId,
        estimatedCurrency,
        arrivalDate,
        departureDate,
        adults,
        childAges,
        // pets parameter for priceDetails is an Integer, we are storing as a boolean
        // https://github.homeawaycorp.com/GraphQL/graphql-traveler-checkout/blob/master/lib/types/CreateCheckoutRequest.gql#L17
        pets: Number(pets)
    };

    let attempt = 0;
    let result = null;
    do {
        attempt++;

        result = await graphqlQuery(
            OPERATION_NAME,
            quoteQuery,
            {createCheckoutRequest: quoteOptions},
            locale,
            true,
            store
        );

        // check the error code on the response and if it's a "429 - Too Many Requests", we sleep for a bit and try again
        // NOTE: this logic allows us to use a low RPS limit for the pricing endpoint while not preventing travelers from getting prices
        if (result.errorCode === 429) {
            await new Promise((resolve) => {
                setTimeout(resolve, RETRY_DELAY);
            });

            continue;
        }

        break;
    } while (attempt < MAX_ATTEMPTS);

    // log a warning if we had to retry at least once
    if (attempt > 1) {
        log({
            message: `Attempted GQL quote operation times="${attempt}" finalErrorCode="${result.errorCode ||
                ''}"`
        });
    }

    return result;
}

/* eslint-enable complexity */
export default quoteGraphql;
export {quoteGraphql, quoteQuery, OPERATION_NAME};
